<script setup lang="ts">
import { watchDebounced } from '@vueuse/core'
import {
  useNavQuery,
  useNewProductsQuery,
  useSearchOverlayQuery,
} from '~~/app/composables/queries/useStoreApi'

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [boolean] }>()

const brand = useBrand()
const router = useRouter()
const MAX_QUERY_LENGTH = 100
const inputRef = ref<HTMLInputElement | null>(null)
const inputValue = ref('')
const debouncedQuery = ref('')
const autocompleteVisible = ref(false)

watchDebounced(
  inputValue,
  (val) => { debouncedQuery.value = val.trim() },
  { debounce: 280, maxWait: 800 },
)

const { data: nav } = useNavQuery()
const { data: newProducts } = useNewProductsQuery({ currentUrl: '/nowosci' }, { ssr: false })
const { data: searchData, isLoading: searchLoading } = useSearchOverlayQuery(debouncedQuery)

const popularSearches = computed(() =>
  nav.value?.items[0]?.children.slice(0, 8).map(c => c.label) ?? [],
)

const hasQuery = computed(() => debouncedQuery.value.length > 1)

const featuredProducts = computed(() =>
  newProducts.value?.products.slice(0, 2) ?? [],
)

const autocompleteItems = computed(() => {
  if (!hasQuery.value || !autocompleteVisible.value) return []
  const cats = searchData.value?.topCategories?.slice(0, 3) ?? []
  return [debouncedQuery.value, ...cats].filter(Boolean).slice(0, 4)
})

// Dwa pełne rzędy wyników na desktopie: 8 przy 4 kolumnach, 6 przy 3 (proles).
const resultColumns = brand.search?.overlayColumns ?? 4

const resultProducts = computed(() =>
  hasQuery.value ? (searchData.value?.products.slice(0, resultColumns * 2) ?? []) : [],
)

// Nowa fraza wpisana, ale debounce jeszcze nie wystartował zapytania —
// traktujemy jak ładowanie, by uniknąć migotania „Brak wyników" w oknie debounce.
const searchPending = computed(() => {
  const typed = inputValue.value.trim()
  return typed.length > 1 && typed !== debouncedQuery.value
})

// Skeletony pokazujemy tylko gdy realnie czekamy na wyniki, których jeszcze nie ma.
// resultProducts.length === 0 chroni przed nadpisaniem aktualnie widocznych wyników
// podczas tła/odświeżenia (np. refine zapytania przy zachowanych wynikach).
const showSearchLoading = computed(() =>
  hasQuery.value
  && resultProducts.value.length === 0
  && (searchLoading.value || searchPending.value),
)

watch(debouncedQuery, (q) => {
  if (q.length > 1) autocompleteVisible.value = true
})

function close() {
  emit('update:modelValue', false)
}

function applySearch(phrase: string) {
  inputValue.value = phrase
  debouncedQuery.value = phrase
  autocompleteVisible.value = false
  inputRef.value?.focus()
}

function submitSearch() {
  const q = inputValue.value.trim()
  if (!q) return
  autocompleteVisible.value = false
  router.push({ path: '/search', query: { q } })
  close()
}

function clearInput() {
  inputValue.value = ''
  debouncedQuery.value = ''
  autocompleteVisible.value = false
  nextTick(() => inputRef.value?.focus())
}

function onInputBlur() {
  setTimeout(() => { autocompleteVisible.value = false }, 160)
}

function onInputFocus() {
  if (hasQuery.value) autocompleteVisible.value = true
}

watch(() => props.modelValue, (open) => {
  if (!import.meta.client) return
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) {
    autocompleteVisible.value = false
    nextTick(() => inputRef.value?.focus())
  }
  else {
    inputValue.value = ''
    debouncedQuery.value = ''
    autocompleteVisible.value = false
  }
})

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.modelValue) close()
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
  if (import.meta.client) document.body.style.overflow = ''
})
</script>

<template>
  <Transition name="so">
    <div v-if="modelValue" class="search-overlay">
      <div class="search-overlay__backdrop" @click="close" />
      <div class="search-overlay__panel">

        <!-- Nav row — replaces the site-nav visually -->
        <div class="search-overlay__nav">
          <div class="search-overlay__nav-inner">

            <NuxtLink
              to="/"
              class="search-overlay__logo"
              :aria-label="`${brand.name} – strona główna`"
              @click="close"
            >
              <img
                :src="brand.logo.src"
                alt=""
                width="151"
                height="28"
                class="search-overlay__logo-img"
              />
            </NuxtLink>

            <div class="search-overlay__input-wrap">
              <form class="search-overlay__form" @submit.prevent="submitSearch">
                <svg class="search-overlay__input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  ref="inputRef"
                  v-model="inputValue"
                  type="search"
                  class="search-overlay__input"
                  :placeholder="`SZUKAJ W ${brand.name}`"
                  autocomplete="off"
                  :maxlength="MAX_QUERY_LENGTH"
                  @focus="onInputFocus"
                  @blur="onInputBlur"
                />
                <button v-if="inputValue" type="button" class="search-overlay__clear" @click="clearInput">
                  Wyczyść
                </button>
              </form>

              <!-- Autocomplete dropdown -->
              <Transition name="ac">
                <div v-if="autocompleteItems.length" class="search-overlay__autocomplete">
                  <p class="search-overlay__ac-label">Propozycje dla „{{ debouncedQuery }}"</p>
                  <ul class="search-overlay__ac-list">
                    <li v-for="item in autocompleteItems" :key="item">
                      <button
                        type="button"
                        class="search-overlay__ac-item"
                        @mousedown.prevent="applySearch(item)"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                          <circle cx="11" cy="11" r="8" />
                          <path d="m21 21-4.35-4.35" />
                        </svg>
                        {{ item }}
                      </button>
                    </li>
                  </ul>
                </div>
              </Transition>
            </div>

            <button type="button" class="search-overlay__close" aria-label="Zamknij" @click="close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Panel body -->
        <div class="search-overlay__body">
          <div class="container">

            <!-- Empty state: popular + featured -->
            <Transition name="state" mode="out-in">
              <div v-if="!hasQuery" key="empty" class="search-overlay__empty-state">
                <div class="search-overlay__popular">
                  <p class="search-overlay__section-title">POPULARNE WYSZUKIWANIA</p>
                  <div class="search-overlay__tags">
                    <button
                      v-for="tag in popularSearches"
                      :key="tag"
                      type="button"
                      class="search-overlay__tag"
                      @click="applySearch(tag)"
                    >
                      {{ tag }}
                    </button>
                  </div>
                </div>

                <div class="search-overlay__featured">
                  <p class="search-overlay__section-title">NAJCZĘŚCIEJ WYSZUKIWANE</p>
                  <div class="search-overlay__featured-grid">
                    <ProductCard
                      v-for="product in featuredProducts"
                      :key="product.id"
                      :product="product"
                      @click="close"
                    />
                  </div>
                </div>
              </div>

              <!-- Results state -->
              <div v-else key="results" class="search-overlay__results-state">
                <div
                  v-if="resultProducts.length"
                  class="search-overlay__results-grid"
                  :class="{ 'search-overlay__results-grid--cols-3': resultColumns === 3 }"
                >
                  <ProductCard
                    v-for="product in resultProducts"
                    :key="product.id"
                    :product="product"
                    @click="close"
                  />
                </div>

                <!-- Loading state: skeletony na czas zapytania „on the fly" -->
                <div
                  v-else-if="showSearchLoading"
                  class="search-overlay__results-grid search-overlay__results-grid--loading"
                  :class="{ 'search-overlay__results-grid--cols-3': resultColumns === 3 }"
                  aria-busy="true"
                  aria-label="Wyszukiwanie produktów"
                >
                  <div
                    v-for="n in resultColumns"
                    :key="n"
                    class="search-overlay__skeleton"
                    aria-hidden="true"
                  >
                    <div class="search-overlay__skeleton-image" />
                    <div class="search-overlay__skeleton-info">
                      <div class="search-overlay__skeleton-line search-overlay__skeleton-line--short" />
                      <div class="search-overlay__skeleton-line" />
                      <div class="search-overlay__skeleton-line search-overlay__skeleton-line--price" />
                    </div>
                  </div>
                </div>

                <p v-else class="search-overlay__no-results">
                  Brak wyników dla „{{ debouncedQuery }}"
                </p>

                <div v-if="resultProducts.length" class="search-overlay__see-all">
                  <NuxtLink
                    :to="`/search?q=${encodeURIComponent(debouncedQuery)}`"
                    class="search-overlay__see-all-link"
                    @click="close"
                  >
                    Pokaż wszystkie wyniki
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </NuxtLink>
                </div>
              </div>
            </Transition>

          </div>
        </div>

      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* ── Transitions ── */
.so-enter-active { transition: opacity 200ms ease; }
.so-leave-active { transition: opacity 140ms ease; }
.so-enter-from, .so-leave-to { opacity: 0; }
.so-enter-active .search-overlay__panel { transition: transform 200ms ease; }
.so-enter-from .search-overlay__panel { transform: translateY(-6px); }

.ac-enter-active, .ac-leave-active { transition: opacity 120ms ease, transform 120ms ease; }
.ac-enter-from, .ac-leave-to { opacity: 0; transform: translateY(-4px); }

.state-enter-active, .state-leave-active { transition: opacity 120ms ease; }
.state-enter-from, .state-leave-to { opacity: 0; }

/* ── Root: transparent, click-to-close ── */
.search-overlay {
  position: fixed;
  inset: 0;
  z-index: 900;
}

.search-overlay__backdrop {
  position: absolute;
  inset: 0;
}

/* ── Panel ── */
.search-overlay__panel {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: var(--color-white);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.08);
}

/* ── Nav row (replaces site-nav) ── */
.search-overlay__nav {
  height: 70px;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  align-items: center;
}

.search-overlay__nav-inner {
  width: 100%;
  max-width: var(--container, 1400px);
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 40px;
  height: 100%;
}

.search-overlay__logo {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  line-height: 1;
  text-decoration: none;
}

.search-overlay__logo-img {
  display: block;
  height: 1.75rem;
  width: auto;
}

/* Logo DISCANTO (8,8:1) wymiarowane szerokością logo lavmaga — jak w SiteHeader. */
[data-brand="discanto"] .search-overlay__logo-img {
  width: 151px;
  height: auto;
  aspect-ratio: 799 / 91;
}

/* Proles: to samo logo co w nagłówku (44 px, plik przycięty do rysunku
   30.10.2026). Przy wspólnych 28 px i starym pliku z marginesem widoczne logo
   miało ~16 px — „w wyszukiwarce jeszcze mniejsze" (spotkanie 30.10.2026). */
[data-brand="proles"] .search-overlay__logo-img {
  height: 2.75rem;
}

/* Większe logo zabiera miejsce polu: <input type="search"> nie zwęża się
   poniżej domyślnej szerokości (~175 px) bez min-width: 0, więc na telefonie
   (360–414 px) przycisk X wypadał poza ekran — overlay jest fixed, nie dało się
   go zamknąć. (Lavmag ma ten sam problem przy 360 px już dziś — osobna sprawa.) */
[data-brand="proles"] .search-overlay__input-wrap,
[data-brand="proles"] .search-overlay__input {
  min-width: 0;
}

/* ── Input wrap ── */
.search-overlay__input-wrap {
  flex: 1;
  position: relative;
}

.search-overlay__form {
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid var(--color-black);
  height: 42px;
}

.search-overlay__input-icon {
  flex-shrink: 0;
  color: var(--color-muted);
}

.search-overlay__input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.9375rem;
  font-family: var(--font-body);
  font-weight: var(--font-weight-light);
  letter-spacing: 0.04em;
  color: var(--color-black);
  padding: 0;
  height: 100%;
}
.search-overlay__input::placeholder { color: var(--color-muted); }
.search-overlay__input:focus { outline: none; }
.search-overlay__input::-webkit-search-cancel-button { display: none; }

.search-overlay__clear {
  background: none;
  border: none;
  font-family: var(--font-body);
  font-size: 0.75rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
  text-decoration: underline;
  flex-shrink: 0;
  transition: color var(--transition-fast);
}
.search-overlay__clear:hover { color: var(--color-black); }

/* ── Close button ── */
.search-overlay__close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-black);
  flex-shrink: 0;
  transition: opacity var(--transition-fast);
}
.search-overlay__close:hover { opacity: 0.45; }

/* ── Autocomplete dropdown ── */
.search-overlay__autocomplete {
  position: absolute;
  top: calc(100% + 1px);
  left: 0;
  right: 0;
  background: var(--color-white);
  border: 1px solid var(--color-border);
  border-top: none;
  z-index: 10;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.07);
}

.search-overlay__ac-label {
  margin: 0;
  padding: 12px 16px 8px;
  font-size: 0.625rem;
  font-weight: var(--font-weight-normal);
  letter-spacing: 0.12em;
  color: var(--color-muted);
  text-transform: uppercase;
}

.search-overlay__ac-list {
  list-style: none;
  padding: 0 0 8px;
  margin: 0;
}

.search-overlay__ac-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 11px 16px;
  background: none;
  border: none;
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  cursor: pointer;
  text-align: left;
  transition: background var(--transition-fast);
}
.search-overlay__ac-item:hover { background: var(--color-bg); }
.search-overlay__ac-item svg { color: var(--color-muted); flex-shrink: 0; }

/* ── Panel body ── */
.search-overlay__body {
  max-height: calc(100svh - 70px);
  overflow-y: auto;
  padding: 36px 0 56px;
}

/* ── Section title ── */
.search-overlay__section-title {
  margin: 0 0 18px;
  font-size: 0.625rem;
  font-weight: var(--font-weight-normal);
  letter-spacing: 0.12em;
  color: var(--color-muted);
}

/* ── Empty state ── */
.search-overlay__empty-state {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 72px;
  align-items: start;
}

.search-overlay__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.search-overlay__tag {
  background: none;
  border: 1px solid var(--color-border-md);
  padding: 7px 18px;
  font-family: var(--font-body);
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  cursor: pointer;
  border-radius: 20px;
  transition: background var(--transition-fast), border-color var(--transition-fast);
  white-space: nowrap;
}
.search-overlay__tag:hover {
  background: var(--color-bg);
  border-color: var(--color-black);
}

.search-overlay__featured-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4px;
}

/* ── Results state ── */
.search-overlay__results-state {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.search-overlay__results-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
}

/* Marka z `search.overlayColumns: 3` (proles). Przed progami @media, żeby
   na węższych ekranach nadal działały ich 3 i 2 kolumny. */
.search-overlay__results-grid--cols-3 {
  grid-template-columns: repeat(3, 1fr);
}

.search-overlay__results-grid :deep(.product-card__info) {
  padding: 8px 10px;
  gap: 4px;
}

.search-overlay__results-grid :deep(.product-card__name),
.search-overlay__results-grid :deep(.product-card__price),
.search-overlay__results-grid :deep(.product-card__sizes) {
  font-size: 0.75rem;
}

.search-overlay__results-grid :deep(.product-card__sizes-chips) {
  display: none;
}

/* ── Loading skeletons ── */
.search-overlay__results-grid--loading {
  align-items: start;
}

.search-overlay__skeleton {
  display: flex;
  flex-direction: column;
}

.search-overlay__skeleton-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 10px 14px;
}

.search-overlay__skeleton-image,
.search-overlay__skeleton-line {
  background: var(--color-border-md);
  animation: search-overlay-pulse 1.5s ease-in-out infinite;
}

.search-overlay__skeleton-image {
  width: 100%;
  /* Ta sama proporcja co zdjęcie ProductCard — bez skoku po załadowaniu. */
  aspect-ratio: var(--product-card-img-ratio);
}

.search-overlay__skeleton-line {
  height: 10px;
}

.search-overlay__skeleton-line--short {
  width: 50%;
}

.search-overlay__skeleton-line--price {
  width: 32%;
}

@keyframes search-overlay-pulse {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }

  100% {
    opacity: 1;
  }
}

.search-overlay__no-results {
  margin: 0;
  padding: 48px 0;
  font-size: 1rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
}

/* ── See all link ── */
.search-overlay__see-all {
  display: flex;
  justify-content: center;
}

.search-overlay__see-all-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  text-decoration: none;
  letter-spacing: 0.06em;
  border-bottom: 1px solid currentColor;
  padding-bottom: 2px;
  transition: opacity var(--transition-fast);
}
.search-overlay__see-all-link:hover { opacity: 0.55; }

/* ── Responsive ── */
@media (max-width: 1100px) {
  .search-overlay__empty-state { gap: 48px; }
  .search-overlay__results-grid { grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 900px) {
  .search-overlay__empty-state {
    grid-template-columns: 1fr;
    gap: 36px;
  }
  .search-overlay__featured-grid { grid-template-columns: repeat(2, 1fr); }
  .search-overlay__results-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 640px) {
  .search-overlay__nav-inner { gap: 20px; }
  .search-overlay__logo-img { height: 1.5rem; }
  [data-brand="discanto"] .search-overlay__logo-img { width: 129px; }
  /* Mały tablet: 26 px (84 px szerokości). */
  [data-brand="proles"] .search-overlay__logo-img { height: 1.625rem; }
}

/* Telefon: w wierszu są lupa, „Wyczyść", X i odstępy po 20 px — z logo pole
   wpisywania miało 45 px przy 360 px i 5 px przy 320 px (z „kubek termiczny"
   widać było „bek te"). W nakładce logo jest dekoracją, więc proles chowa je
   do 480 px; uwaga „logo w wyszukiwarce większe" dotyczyła desktopu. */
@media (max-width: 480px) {
  [data-brand="proles"] .search-overlay__logo {
    display: none;
  }
}
</style>
